import { Mesh, type Group } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import type { Entity } from "../../core/entity/index"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import useAddToPropertyLightsBlinkingManager from "../../event/GameObject3D/PropertyLightsBlinking/useAddToPropertyLightsBlinkingManager"
import getEntityWindowLightMaterial from "../PlayerThreeObject/getEntityWindowLightMaterial"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseMeshGeometryNormals from "./creaseMeshGeometryNormals"
import getSceneWindows from "./getSceneWindows"
import isMesh from "./isMesh"
import onGltfLoaderError from "./onGltfLoaderError"
import setListVisible from "./setListVisible"

export default function makePropertyObject3DFactory<T extends Entity>(GLTF_Model: string) {
  return function makePropertyObject3D(
    engine: Engine,
    ent: EntityGraphNode<T>,
    callback: (newGroup: Group | Mesh) => void
  ): void {
    const addToPropertyLightsBlinkingManager = useAddToPropertyLightsBlinkingManager(engine)
    return appGltfLoader.load(
      GLTF_Model,
      function (ModelObj) {
        // console.log(entityDisplayName(ent), "ModelObj", ModelObj)
        const { scene } = ModelObj
        applyPlayerColors(engine, scene, ent)

        // console.log(entityDisplayName(ent.raw()))
        // printObjectHierarchy(scene)

        const darkWindows = getSceneWindows(scene)

        if (darkWindows.length > 0) {
          const lightMaterial = getEntityWindowLightMaterial()
          const lightWindows = darkWindows.map((darkMesh) => {
            const lightMesh = darkMesh.clone()
            lightMesh.material = lightMaterial
            darkMesh.parent!.add(lightMesh)
            return lightMesh
          })

          addToPropertyLightsBlinkingManager(scene, (lightsOn: boolean) => {
            // lightsOn = !true
            setListVisible(darkWindows, !lightsOn)
            setListVisible(lightWindows, lightsOn)
          })
        }

        scene.traverse((obj) => {
          if (isMesh(obj) && obj.userData.CREASE) {
            creaseMeshGeometryNormals(obj)
          }
        })

        mountOnFrameUpdateToObject3D(engine, scene, (elapsedMs: number) => {
          applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
        })

        callback(scene)
      },
      undefined,
      onGltfLoaderError
    )
  }
}
